// src/utils/receiptToImage.js
// Convierte el HTML de un comprobante a PNG, lo descarga y opcionalmente
// abre WhatsApp Web con el número del cliente pre-llenado.

const loadHtml2Canvas = () =>
  new Promise((resolve, reject) => {
    if (window.html2canvas) { resolve(window.html2canvas); return; }
    const script   = document.createElement('script');
    script.src     = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
    script.onload  = () => resolve(window.html2canvas);
    script.onerror = () => reject(new Error('No se pudo cargar html2canvas'));
    document.head.appendChild(script);
  });

/**
 * Limpia un número de teléfono y lo convierte al formato internacional MX (52XXXXXXXXXX).
 * Acepta: 10 dígitos, con +52, con 52, con espacios/guiones.
 */
const formatPhoneMX = (raw = '') => {
  const digits = String(raw).replace(/\D/g, '');
  if (digits.startsWith('521') && digits.length === 13) return digits;          // ya completo con 521
  if (digits.startsWith('52')  && digits.length === 12) return `${digits}`;     // 52 + 10 dígitos
  if (digits.length === 10)                              return `52${digits}`;   // solo 10 dígitos
  return null; // no reconocido
};

/**
 * Abre WhatsApp Web con el número y mensaje pre-llenados.
 * El usuario adjunta la imagen manualmente.
 *
 * @param {string} phone    - Teléfono del destinatario (cualquier formato MX)
 * @param {string} message  - Texto pre-llenado
 */
export const openWhatsApp = (phone, message = '') => {
  const formatted = formatPhoneMX(phone);
  if (!formatted) { console.warn('Número de teléfono no válido:', phone); return; }
  const url = `https://wa.me/${formatted}?text=${encodeURIComponent(message)}`;
  window.open(url, '_blank', 'noopener,noreferrer');
};

/**
 * Recibe el HTML completo de un comprobante, lo renderiza en un iframe oculto,
 * captura con html2canvas, descarga como PNG y abre WhatsApp si se proporciona teléfono.
 *
 * @param {string} html      - HTML completo del comprobante
 * @param {string} filename  - Nombre del archivo sin extensión (ej: "POS-000042")
 * @param {object} [wa]      - Opcional: { phone, studentName, type }
 *                             type: 'venta' | 'registro' | 'festival'
 */
export const downloadReceiptAsImage = async (html, filename = 'comprobante', wa = null) => {
  let iframe;
  try {
    const html2canvas = await loadHtml2Canvas();

    // 1. Crear iframe oculto
    iframe = document.createElement('iframe');
    Object.assign(iframe.style, {
      position:   'fixed',
      top:        '-9999px',
      left:       '-9999px',
      width:      '640px',
      height:     '1200px',
      border:     'none',
      visibility: 'hidden',
    });
    document.body.appendChild(iframe);

    // 2. Escribir HTML en el iframe y esperar que cargue
    await new Promise(resolve => {
      iframe.onload = resolve;
      iframe.contentDocument.open();
      iframe.contentDocument.write(html);
      iframe.contentDocument.close();
    });

    // 3. Esperar que fuentes y estilos terminen
    await new Promise(r => setTimeout(r, 600));

    // 4. Capturar con html2canvas
    const body   = iframe.contentDocument.body;
    const canvas = await html2canvas(body, {
      scale:           2,
      useCORS:         true,
      allowTaint:      true,
      backgroundColor: '#f3f4f6',
      width:           640,
      windowWidth:     640,
      logging:         false,
    });

    // 5. Descargar PNG
    const link    = document.createElement('a');
    link.download = `${filename}.png`;
    link.href     = canvas.toDataURL('image/png');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // 6. Abrir WhatsApp si se proporcionó teléfono
    if (wa?.phone) {
      const typeLabel = {
        venta:    'Comprobante de Pago',
        registro: 'Comprobante de Inscripción',
        festival: 'Comprobante de abono al festival',
      }[wa.type] || 'comprobante';

      const msg =
        `Hola${wa.studentName ? ` ${wa.studentName}` : ''}, ` +
        `te compartimos tu ${typeLabel}. ` ;

      // Pequeña pausa para que la descarga inicie antes de redirigir
      setTimeout(() => openWhatsApp(wa.phone, msg), 800);
    }

  } catch (err) {
    console.error('Error al generar imagen del comprobante:', err);
    alert('No se pudo generar la imagen. Intenta de nuevo.');
  } finally {
    if (iframe && iframe.parentNode) {
      document.body.removeChild(iframe);
    }
  }
};
